import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, HeartPulse, Mail } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLang } from "../../context/LangContext";
import { supabase } from "../../lib/supabase";
import { getV2Copy } from "../content";
import { getSafeAuthMessage } from "../lib/v2AuthUtils";

export default function V2ForgotPassword() {
  const { lang } = useLang();
  const copy = getV2Copy(lang);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    const normalizedEmail = String(email || "").trim().toLowerCase();
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (resetError) {
      const message = getSafeAuthMessage(resetError, copy.forgotSentNotes);
      if (message === copy.forgotSentNotes) {
        setSent(true);
      } else {
        setError(message);
      }
      return;
    }

      setSent(true);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-white">
            <HeartPulse className="h-6 w-6" />
          </div>
          <span className="text-2xl font-extrabold" style={{ fontFamily: "Manrope" }}>
            Dialysis Diet Pro
          </span>
        </Link>

        <section className="rounded-lg border border-border bg-card p-6">
          {!sent ? (
            <>
              <div className="mb-6 flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold">{copy.forgotTitle}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">{copy.forgotNotes}</p>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="v2-forgot-email">{copy.email}</Label>
                  <Input
                    id="v2-forgot-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Sending..." : copy.sendResetLink}
                </Button>
                {error && <p className="text-sm font-medium text-destructive">{error}</p>}
              </form>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle2 className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-xl font-bold">{copy.forgotSentTitle}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{copy.forgotSentNotes}</p>
            </div>
          )}

          <div className="mt-6 text-center text-sm">
            <Link to="/login" className="font-semibold text-primary hover:underline">
              {copy.backToLogin}
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
